import type { Rule } from '@/api/types'
import { BandBadge } from '@/components/common/Badge'
import { formatDate } from '@/components/common/format'
import { celToPredicates, describeAction, fieldLabel, OP_LABELS, ruleSentence } from './ruleCel'

// quantum_risk value → tone (vulnerable worst → safe best); mirrors the risk color language.
const RISK_TONE: Record<string, string> = {
  vulnerable: 'bg-error text-white dark:bg-error dark:text-white',
  partial: 'bg-error/15 text-error dark:bg-error/25 dark:text-red-300',
  hybrid: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  safe: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  unknown: 'bg-container-light text-subtle-light dark:bg-subtle-dark/15 dark:text-subtle-dark',
}

// CondValue colors known risk enums so `is critical` / `is vulnerable` scan the same as
// action band badges; other values render as plain mono.
function CondValue({ field, value }: { field: string; value: string }) {
  if (field.endsWith('.band') || field.endsWith('.criticality')) return <BandBadge band={value} />
  if (field.endsWith('.quantum_risk')) {
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${RISK_TONE[value] ?? RISK_TONE.unknown}`}
      >
        {value}
      </span>
    )
  }
  return <span className="font-mono text-primary dark:text-secondary">{value}</span>
}

// Entity icon per CEL field namespace, so `algorithm` vs `criticality` reads at a glance.
const NS_ICON: Record<string, string> = {
  asset: 'memory',
  inventory: 'inventory_2',
  ctx: 'functions',
}
const nsIcon = (path: string) => NS_ICON[path.split('.')[0]] ?? 'label'

// Chip style per action group — scoring actions mutate the score, evidence-only actions
// record obligations. Distinct tints/icons make the difference scannable.
const GROUP_STYLE: Record<string, { cls: string; icon: string }> = {
  Scoring: { cls: 'bg-primary/10 text-primary dark:text-secondary', icon: 'tune' },
  'Evidence-only': {
    cls: 'bg-secondary/15 text-text-light dark:text-text-dark',
    icon: 'fact_check',
  },
  unknown: { cls: 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark', icon: 'help' },
}

// RulePreview renders a rule's condition + actions as readable chips (read-only).
// Simple AND-of-comparisons `when` expressions decompose into predicate chips; anything
// using OR / parentheses / function calls falls back to a raw CEL badge.
export function RulePreview({ rule }: { rule: Rule }) {
  const preds = celToPredicates(rule.when_expr)
  const actions = rule.then_actions ?? []
  const effFrom = rule.effective_from ? formatDate(rule.effective_from) : null
  const effTo = rule.effective_to ? formatDate(rule.effective_to) : null

  return (
    <div className="space-y-2">
      {/* Plain-English summary — grasp intent without decoding chips */}
      <p className="text-sm text-text-light dark:text-text-dark">{ruleSentence(rule)}</p>

      {/* When */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark">
          When
        </span>
        {preds === null ? (
          <>
            <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
              Advanced CEL
            </span>
            <code className="rounded-md bg-container-light dark:bg-container-dark px-2 py-0.5 font-mono text-xs text-text-light dark:text-text-dark">
              {rule.when_expr}
            </code>
          </>
        ) : preds.length === 0 ? (
          <span className="text-xs italic text-subtle-light dark:text-subtle-dark">
            matches all assets
          </span>
        ) : (
          preds.map((p, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && (
                <span className="text-[10px] font-semibold text-subtle-light dark:text-subtle-dark">
                  AND
                </span>
              )}
              <span
                title={`${p.field} ${p.op} ${p.value}`}
                className="inline-flex items-center gap-1 rounded-md bg-primary/5 px-2 py-0.5 text-xs text-text-light dark:text-text-dark"
              >
                <span className="material-icons text-sm text-subtle-light dark:text-subtle-dark">
                  {nsIcon(p.field)}
                </span>
                <span className="font-medium">{fieldLabel(p.field)}</span>
                <span className="text-subtle-light dark:text-subtle-dark">
                  {OP_LABELS[p.op] ?? p.op}
                </span>
                <CondValue field={p.field} value={p.value} />
              </span>
            </span>
          ))
        )}
      </div>

      {/* Then */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark">
          Then
        </span>
        {actions.length === 0 ? (
          <span className="text-xs italic text-subtle-light dark:text-subtle-dark">no actions</span>
        ) : (
          actions.map((a, i) => {
            const { label, detail, group, param } = describeAction(a)
            const style = GROUP_STYLE[group] ?? GROUP_STYLE.unknown
            // Free-text params (guidance / notes) can be long — let them wrap full-width
            // instead of truncating; short params stay inline.
            const longText = param === 'textarea' || param === 'note'
            return (
              <span
                key={i}
                className={`inline-flex ${longText ? 'w-full items-start' : 'items-center'} gap-1 rounded-md px-2 py-0.5 text-xs ${style.cls}`}
              >
                <span className="material-icons text-sm opacity-70">{style.icon}</span>
                <span className="font-medium whitespace-nowrap">{label}</span>
                {detail &&
                  (param === 'band' ? (
                    <BandBadge band={detail} />
                  ) : (
                    <>
                      <span className="opacity-60">→</span>
                      <span
                        className={
                          longText
                            ? 'whitespace-pre-wrap break-words font-mono'
                            : 'max-w-[220px] truncate font-mono'
                        }
                      >
                        {detail}
                      </span>
                    </>
                  ))}
              </span>
            )
          })
        )}
      </div>

      {/* Effective window (temporal scope) */}
      {(effFrom || effTo) && (
        <div className="flex items-center gap-1.5 text-xs text-subtle-light dark:text-subtle-dark">
          <span className="material-icons text-sm">event</span>
          <span>
            {effFrom ?? '—'} → {effTo ?? 'open'}
          </span>
        </div>
      )}
    </div>
  )
}
